/**
 * kw-map (레인 D) — 현장·시군구마다 «사람들이 실제로 치는 검색어» 지도. 다른 레인(블로그·카페·상세·허브)이 import 만 한다.
 *
 * ── 공개 계약(시그니처 고정 — 바꾸지 말 것) ─────────────────────────────
 *   type KwAngle · interface KwAnglePlan · interface SiteKeywordPlan
 *   loadSiteKeywordPlan(sb, siteId)               → SiteKeywordPlan        (던지지 않는다 · 모름 = names [] 인 빈 계획)
 *   loadDistrictKeywordPlan(sb, sido, sigungu)    → { primary, secondaries } (던지지 않는다 · 모름 = 실측 순서 사전값)
 *   COMPETITOR_FACETS                             경쟁 상위 문서가 다루는 항목(많은 순)
 *   imageAlt(siteName, angle, what)               이미지 alt(현장명 + 각도어 + 무엇) — 스터핑 없이 한 번씩
 *
 * 재료(DB)
 *   · site_kick_keywords — 현장명 + 꼬리 실측 검색량(네이버 검색광고 keywordstool, kick-keywords 크론)
 *   · naver_keyword_pool — context='site'(seed = 현장 id) 연관어 · context='region' 지역 꼬리 실측 검색량
 *   · apt_sites — 이름·표시명·별칭·동·구역명
 * 측정이 없으면 «전역 실측 순서»(현장명 뒤 꼬리 검색량 합계 — 아래 GLOBAL_MODIFIER_VOLUME)로 채운다.
 *
 * ⛔ 매매·전세·월세 등 매물형 말은 쓰지 않는다(우리가 답할 수 없는 검색 의도).
 * ⛔ 사실 관문은 여기서 걸지 않는다 — 「분양가」 각도를 쓰는 호출부가 값 유무를 보고 「공개 전 — 정해지면 알림」 식으로 쓴다.
 * 순수 함수(build*) 와 DB 로더(load*) 를 나눈다(시험은 순수 쪽을 바로 부른다).
 */
import {
  buildSiteProfile, kickNameOf, kickNorm, parenZoneOf, stripParens, zoneAliasOf, KICK_BRANDS, type KickSiteInput,
} from '@/lib/seo/kick-keywords-collect';
import { spacedPhrase } from '@/lib/seo/kick-keywords';
import { parseNameLead } from '@/lib/seo/name-lead'; // name-lead
import { isProvisionalSiteName } from '@/lib/apt/provisional-name';

/* ───────────────────────────── 계약 타입 ───────────────────────────── */

export type KwAngle = 'price' | 'model_house' | 'schedule' | 'progress' | 'move_in' | 'location' | 'competition' | 'presale_right' | 'unsold';
export interface KwAnglePlan { angle: KwAngle; primary: string; volume: number | null; secondaries: string[] }
export interface SiteKeywordPlan { siteId: string; names: string[]; angles: KwAnglePlan[]; regionTerms: string[] }

export const KW_ANGLES: readonly KwAngle[] = ['price', 'model_house', 'schedule', 'progress', 'move_in', 'location', 'competition', 'presale_right', 'unsold'];

/** 경쟁 상위 문서(kw_compete_docs.subtopics)가 다루는 항목 — 많은 순(실측). */
export const COMPETITOR_FACETS: readonly string[] = [
  'units', 'price', 'schedule', 'location', 'unsold', 'notice', 'trade', 'types', 'model_house',
  'builder', 'competition', 'move_in', 'winner', 'contract', 'school', 'regulation', 'options', 'mgmt',
];

/* ───────────────────────────── 사전 ───────────────────────────── */

/** 각도 → 꼬리(앞이 대표 — 측정이 없을 때 이 순서). */
export const ANGLE_MODIFIERS: Readonly<Record<KwAngle, readonly string[]>> = {
  price: ['분양가', '시세', '평당가', '가격'],
  model_house: ['모델하우스', '견본주택'],
  schedule: ['청약', '청약일정', '분양일정', '일반분양', '분양', '모집공고'],
  progress: ['재개발', '재건축', '관리처분', '사업시행인가', '이주', '진행상황'],
  move_in: ['입주', '입주일', '입주예정'],
  location: ['위치', '입지', '학군', '교통'],
  competition: ['경쟁률', '당첨', '당첨자발표', '커트라인'],
  presale_right: ['분양권', '전매', '프리미엄'],
  unsold: ['미분양', '무순위', '줍줍', '잔여세대', '선착순'],
};

/** 각도 대표어(alt·질문 문형). */
export const ANGLE_TERM: Readonly<Record<KwAngle, string>> = {
  price: '분양가', model_house: '모델하우스', schedule: '청약', progress: '재개발', move_in: '입주',
  location: '위치', competition: '경쟁률', presale_right: '분양권', unsold: '미분양',
};

/**
 * 현장명 뒤 꼬리 월간 검색량 합계(site_kick_keywords 실측 · 측정 없는 현장의 순서 정하기에만 쓴다 — 본문에 수치로 쓰지 말 것).
 */
export const GLOBAL_MODIFIER_VOLUME: Readonly<Record<string, number>> = {
  분양가: 18690, 재개발: 15650, 재건축: 7340, 모델하우스: 4450, 청약: 2690, 위치: 1160, 분양권: 920,
  미분양: 280, 일반분양: 270, 분양일정: 210, 입주: 200, 경쟁률: 200, 시세: 160,
};

const MOD_ANGLE: ReadonlyMap<string, KwAngle> = new Map(
  KW_ANGLES.flatMap((a) => ANGLE_MODIFIERS[a].map((m) => [m, a] as [string, KwAngle])),
);

/** 꼬리 → 각도(사전에 없으면 null). 「분양」 처럼 짧은 말은 정확히 같을 때만. */
export function angleOfModifier(mod: string | null | undefined): KwAngle | null {
  const m = String(mod ?? '').replace(/\s+/g, '');
  if (!m) return null;
  if (MOD_ANGLE.has(m)) return MOD_ANGLE.get(m)!;
  for (const a of KW_ANGLES) for (const x of ANGLE_MODIFIERS[a]) if (x.length >= 3 && m.endsWith(x)) return a;
  return null;
}

/** 각도 전역 점수 = 그 각도 꼬리 중 가장 큰 전역 검색량. */
export function globalAngleScore(a: KwAngle): number {
  return Math.max(0, ...ANGLE_MODIFIERS[a].map((m) => GLOBAL_MODIFIER_VOLUME[m] ?? 0));
}
/** 전역 실측 순서(분양가 > 재개발 > 모델하우스 > 청약 > 위치 > 분양권 > 미분양 > 입주 > 경쟁률). */
export const GLOBAL_ANGLE_ORDER: readonly KwAngle[] = [...KW_ANGLES].sort((a, b) => globalAngleScore(b) - globalAngleScore(a) || KW_ANGLES.indexOf(a) - KW_ANGLES.indexOf(b));

/** 매물형(우리가 답할 수 없는) 검색어. */
const LISTING_RE = /(매매|전세|월세|임대|매물|급매|경매|원룸|투룸|쓰리룸|빌라|상가|토지|공장|빌딩|단기|촌집|시골집|부동산다나와|교차로)/;
export function isListingTerm(s: string | null | undefined): boolean {
  return LISTING_RE.test(String(s ?? '').replace(/\s+/g, ''));
}

const REDEV_STAGES = new Set(['site_planning', 'union_established', 'constructor_selected', 'plan_approved', 'mgmt_approved', 'relocation', 'demolition']);
const REDEV_TAIL_RE = /(재개발|재건축|정비사업)$/;

/* ───────────────────────────── 이름 ───────────────────────────── */

/** 이름만으로 현장을 못 가리키는 짧은·일반 브랜드(동+브랜드 별칭에 쓰지 않는다). */
const WEAK_BRANDS = new Set(['롯데', '현대', 'sk', '뷰', '스위트', '엘가', '위브', '비스타', '써밋', '더휴', '비발디', '자이', '유보라', '풍경채', '이다음', '내안애']);

const stem = (s: string, re: RegExp): string => { const t = s.replace(re, ''); return t.length >= 2 ? t : s; };
const norm = kickNorm;

/**
 * 현장 이름 후보(공백 유지 · 중복 없음 · 최대 6): 검색 이름 → 구역명(「○○N구역」) → 다른 표시명 → 짧은 별칭 → 동+브랜드.
 * 지역 앞머리를 붙인 표시형(「부산 남구 문현3 재개발」)은 넣지 않는다(사람이 치는 말이 아니다).
 */
export function siteNamesOf(site: KickSiteInput): string[] {
  const out: string[] = [];
  const add = (v: string | null | undefined) => {
    const s = String(v ?? '').replace(/\s+/g, ' ').trim();
    if (!s || s.length < 2 || isProvisionalSiteName(s)) return;
    const k = norm(s);
    if (out.some((o) => norm(o) === k)) return;
    out.push(s);
  };
  /* name-lead (2026-11-02) — 대표 이름 확정값(실측 검색량 우위 · 크론 site-name-lead)이 있으면 그 이름을 맨 앞에, 다른 이름을 둘째에.
   *   블로그·카페·KWX 제목의 대표 검색어(phraseOf names[0])가 이 순서를 따른다. 확정값이 없으면 종전 순서. */
  const nl = parseNameLead((site as { name_lead?: unknown }).name_lead);
  if (nl) { add(nl.lead === 'brand' ? nl.brand : nl.zone); add(nl.lead === 'brand' ? nl.zone : nl.brand); }
  const primary = kickNameOf(site) ?? stripParens(site.name);
  add(primary);
  const prof = buildSiteProfile(site);
  const core = prof?.core ?? primary;
  add(zoneAliasOf(core) ?? parenZoneOf(site.name));
  const sido = String(site.region ?? '').trim();
  const regionLed = (s: string) => !!sido && (s.startsWith(`${sido} `) || s.startsWith(sido));
  for (const c of [stripParens(site.display_name), stripParens(site.name)]) if (c && !regionLed(c)) add(c);
  if (core && norm(core) !== norm(primary) && norm(core).length >= 3) add(core);
  const vars = (Array.isArray(site.name_variants) ? site.name_variants : [])
    .map((v) => stripParens(String(v ?? '')))
    .filter((v) => v.length >= 3 && v.length <= 20 && !regionLed(v))
    .sort((a, b) => a.length - b.length);
  for (const v of vars.slice(0, 2)) add(v);
  // 동 + 브랜드(「범천 힐스테이트」) — 이름에 이미 동 어간이 있으면 같은 말이라 넣지 않는다.
  const dongStem = stem(String(site.dong ?? '').trim(), /(동|리|읍|면|가)$/);
  if (dongStem && dongStem.length >= 2 && !norm(primary).includes(norm(dongStem))) {
    const brand = primary.split(' ').find((w) => {
      const n = norm(w);
      return n.length >= 3 && !WEAK_BRANDS.has(n) && KICK_BRANDS.some((b) => n === b || (n.startsWith(b) && n.length - b.length <= 3));
    });
    if (brand) add(`${dongStem} ${brand}`);
  }
  return out.slice(0, 6);
}

/** 꼬리를 붙일 이름 — 「…재개발」 로 끝나는 이름 대신 구역명 등 꼬리가 없는 이름을 먼저. */
function baseFor(names: readonly string[], mod: string): string {
  const clean = names.find((n) => !REDEV_TAIL_RE.test(n) && !norm(n).endsWith(norm(mod)));
  return clean ?? names[0] ?? '';
}

/** 「{이름} {꼬리}」 — 이름이 이미 그 꼬리로 끝나면 이름만. */
export function phraseOf(names: readonly string[], mod: string): string {
  const b = baseFor(names, mod);
  if (!b) return '';
  return norm(b).endsWith(norm(mod)) ? b : `${b} ${mod}`;
}

/* ───────────────────────────── 현장 계획(순수) ───────────────────────────── */

export interface SiteKwRow { keyword: string; modifier: string | null; volume_total: number | null; is_lt10?: boolean | null }
export interface PoolPhraseRow { keyword: string; display?: string | null; modifier?: string | null; volume_total?: number | null }

export const SECONDARIES_MAX = 6;

/** 정비 현장인가(단계 · 이름 꼬리 · 구역명). */
export function isRedevSite(site: Pick<KickSiteInput, 'name' | 'display_name' | 'lifecycle_stage'>): boolean {
  if (REDEV_STAGES.has(String(site.lifecycle_stage ?? ''))) return true;
  const t = `${site.name ?? ''} ${site.display_name ?? ''}`;
  return /(재개발|재건축|정비사업|\d구역)/.test(t);
}

export function buildSiteKeywordPlan(
  site: KickSiteInput,
  kicks: readonly SiteKwRow[] = [],
  pool: readonly PoolPhraseRow[] = [],
): SiteKeywordPlan {
  const names = siteNamesOf(site);
  const empty: SiteKeywordPlan = { siteId: site.id, names, angles: [], regionTerms: regionTermsOf(site, kicks) };
  if (!names.length) return empty;
  const redev = isRedevSite(site);
  const redevKind = /재건축/.test(`${site.name ?? ''} ${site.display_name ?? ''}`) ? '재건축' : '재개발';

  // 측정 행 → 각도별(검색량 내림)
  const measured = kicks
    .filter((r) => !r.is_lt10 && r.volume_total != null && r.volume_total > 0 && r.modifier && r.modifier !== 'area' && !isListingTerm(r.keyword))
    .sort((a, b) => (b.volume_total ?? 0) - (a.volume_total ?? 0) || a.keyword.localeCompare(b.keyword));
  const byAngle = new Map<KwAngle, Array<{ phrase: string; volume: number }>>();
  for (const r of measured) {
    const a = angleOfModifier(r.modifier);
    if (!a) continue;
    const phrase = spacedPhrase(r.keyword, r.modifier, names);
    (byAngle.get(a) ?? byAngle.set(a, []).get(a)!).push({ phrase, volume: Number(r.volume_total) });
  }
  const poolBy = new Map<KwAngle, string[]>();
  for (const p of pool) {
    const text = String(p.display || p.keyword || '').trim();
    if (!text || isListingTerm(text)) continue;
    const a = angleOfModifier(p.modifier) ?? angleOfModifier(text);
    if (!a) continue;
    (poolBy.get(a) ?? poolBy.set(a, []).get(a)!).push(text);
  }

  const plans: KwAnglePlan[] = [];
  for (const a of KW_ANGLES) {
    const ms = byAngle.get(a) ?? [];
    let primary: string;
    let volume: number | null;
    if (ms.length) {
      primary = ms[0].phrase; volume = ms[0].volume;
    } else {
      const mod = a === 'progress' ? (redev ? redevKind : '진행상황') : ANGLE_MODIFIERS[a][0];
      primary = phraseOf(names, mod); volume = null;
    }
    const seen = new Set<string>([norm(primary)]);
    const secondaries: string[] = [];
    const push = (s: string) => {
      const k = norm(s);
      if (!k || seen.has(k) || secondaries.length >= SECONDARIES_MAX || isListingTerm(s)) return;
      seen.add(k); secondaries.push(s.trim());
    };
    for (const m of ms.slice(1)) push(m.phrase);
    for (const p of poolBy.get(a) ?? []) push(p);
    for (const mod of ANGLE_MODIFIERS[a]) {
      if (a === 'progress' && !redev && (mod === '재개발' || mod === '재건축' || mod === '관리처분' || mod === '사업시행인가' || mod === '이주')) continue;
      if (a === 'progress' && redev && (mod === '재개발' || mod === '재건축') && mod !== redevKind) continue;
      push(phraseOf(names, mod));
    }
    // 다른 이름(구역명·별칭)으로도 같은 꼬리 한 번
    const headMod = ms.length ? (measured.find((r) => spacedPhrase(r.keyword, r.modifier, names) === primary)?.modifier ?? ANGLE_MODIFIERS[a][0]) : (a === 'progress' ? (redev ? redevKind : '진행상황') : ANGLE_MODIFIERS[a][0]);
    for (const n of names.slice(1, 3)) push(norm(n).endsWith(norm(headMod)) ? n : `${n} ${headMod}`);
    plans.push({ angle: a, primary, volume, secondaries });
  }

  // 순서: 측정 검색량 내림 → (측정 없음) 전역 실측 순서. 정비가 아닌 현장의 진행 각도는 맨 뒤.
  const g = (a: KwAngle) => GLOBAL_ANGLE_ORDER.indexOf(a);
  plans.sort((x, y) => {
    const dx = !redev && x.angle === 'progress' ? 1 : 0;
    const dy = !redev && y.angle === 'progress' ? 1 : 0;
    if (dx !== dy) return dx - dy;
    if (x.volume != null || y.volume != null) {
      if (x.volume == null) return 1;
      if (y.volume == null) return -1;
      if (y.volume !== x.volume) return y.volume - x.volume;
    }
    return g(x.angle) - g(y.angle);
  });
  return { siteId: site.id, names, angles: plans, regionTerms: regionTermsOf(site, kicks) };
}

/** 지역어 — 「시도 시군구」 · 시군구 · 「시군구 동」 · 동 · (측정된) 생활권 검색어 상위 2. */
export function regionTermsOf(site: Pick<KickSiteInput, 'region' | 'sigungu' | 'dong'>, kicks: readonly SiteKwRow[] = []): string[] {
  const out: string[] = [];
  const add = (s: string) => { const t = s.replace(/\s+/g, ' ').trim(); if (t && !out.some((o) => norm(o) === norm(t))) out.push(t); };
  const sido = String(site.region ?? '').trim();
  const sgg = String(site.sigungu ?? '').trim();
  const dong = String(site.dong ?? '').trim();
  if (sido && sgg) add(`${sido} ${sgg}`);
  if (sgg) add(sgg);
  if (sgg && dong) add(`${sgg} ${dong}`);
  if (dong) add(dong);
  const area = kicks
    .filter((r) => r.modifier === 'area' && !r.is_lt10 && (r.volume_total ?? 0) > 0 && !isListingTerm(r.keyword))
    .sort((a, b) => (b.volume_total ?? 0) - (a.volume_total ?? 0));
  for (const r of area.slice(0, 2)) add(r.keyword);
  return out;
}

/* ───────────────────────────── 시군구 계획(순수) ───────────────────────────── */

/** 지역 꼬리 실측 순서(naver_keyword_pool modifier 합계 · 매물형 제외). */
export const DISTRICT_GLOBAL_ORDER: readonly string[] = [
  '아파트분양', '분양', '청약', '재개발', '집값', '분양가', '미분양', '신축아파트', '아파트시세', '분양예정', '청약일정', '재건축', '분양일정', '분양권',
];
/** 우리가 답하는 지역 꼬리(이 밖은 시군구 계획에 넣지 않는다). */
export const DISTRICT_MODIFIERS: readonly string[] = [
  ...DISTRICT_GLOBAL_ORDER, '아파트청약', '모델하우스', '무순위', '입주', '입주예정', '신축', '부동산전망',
];
/** 여러 시도에 같은 이름이 있는 시군구 — 시도를 붙인다. */
const AMBIGUOUS_SGG = new Set(['중구', '동구', '서구', '남구', '북구', '강서구', '고성군']);

export interface DistrictPoolRow { keyword: string; display?: string | null; region?: string | null; modifier?: string | null; volume_total?: number | null; lt10?: boolean | null }

/** 시군구 표기 — 「사상구」 · 모호하면 「부산 남구」. */
export function districtPlaceOf(sido: string, sigungu: string): string {
  const s = String(sigungu ?? '').trim();
  const d = String(sido ?? '').trim();
  if (!s) return d;
  return (s.length <= 2 || AMBIGUOUS_SGG.has(s)) && d ? `${d} ${s}` : s;
}

/** naver_keyword_pool.region 대조 키(공백 제거). */
export function districtRegionKeys(sido: string, sigungu: string): string[] {
  const s = norm(sigungu);
  const d = norm(sido);
  const keys = new Set<string>();
  if (!s) return d ? [d] : [];
  const amb = s.length <= 2 || AMBIGUOUS_SGG.has(s);
  if (d) { keys.add(`${d}${s}`); const st = stem(s, /(구|시|군)$/); if (st !== s) keys.add(`${d}${st}`); }
  if (!amb) { keys.add(s); const st = stem(s, /(구|시|군)$/); if (st !== s && st.length >= 2) keys.add(st); }
  return [...keys];
}

export const DISTRICT_PRIMARY_MAX = 5;
export const DISTRICT_SECONDARY_MAX = 12;

export function buildDistrictKeywordPlan(sido: string, sigungu: string, rows: readonly DistrictPoolRow[] = []): { primary: string[]; secondaries: string[] } {
  const place = districtPlaceOf(sido, sigungu);
  if (!place) return { primary: [], secondaries: [] };
  const keys = new Set(districtRegionKeys(sido, sigungu));
  const allowed = new Set(DISTRICT_MODIFIERS);
  const best = new Map<string, number>();
  for (const r of rows) {
    if (r.lt10 || !r.modifier || !allowed.has(r.modifier) || isListingTerm(r.keyword)) continue;
    if (!keys.has(norm(r.region))) continue;
    const v = Number(r.volume_total) || 0;
    if (v <= 0) continue;
    best.set(r.modifier, Math.max(best.get(r.modifier) ?? 0, v));
  }
  const measured = [...best.entries()].sort((a, b) => b[1] - a[1] || DISTRICT_GLOBAL_ORDER.indexOf(a[0]) - DISTRICT_GLOBAL_ORDER.indexOf(b[0])).map(([m]) => m);
  const order = [...measured, ...DISTRICT_GLOBAL_ORDER.filter((m) => !best.has(m))];
  const phrases = order.map((m) => `${place} ${m}`);
  return { primary: phrases.slice(0, DISTRICT_PRIMARY_MAX), secondaries: phrases.slice(DISTRICT_PRIMARY_MAX, DISTRICT_PRIMARY_MAX + DISTRICT_SECONDARY_MAX) };
}

/** 단계 → 대표 각도(alt·제목 앞말 고르기용) — 미분양 = unsold · 정비 = progress · 공사/입주 = move_in · 그 밖 = price. */
export function angleForStage(stage: string | null | undefined): KwAngle {
  const s = String(stage ?? '');
  if (s === 'unsold_active') return 'unsold';
  if (REDEV_STAGES.has(s)) return 'progress';
  if (s === 'construction' || s === 'move_in_started') return 'move_in';
  if (s === 'post_move_in' || s === 'landmark_active') return 'location';
  return 'price';
}

/**
 * 시군구 계획 대표어 → 꼬리 n 개(「아파트분양·청약·재개발」). 계획 문구에서 지역 표기를 뗀 꼬리만 · 같은 말 하나.
 *   「분양」 처럼 다른 꼬리(아파트분양)에 이미 든 짧은 말은 건너뛴다(같은 말 반복 방지).
 */
export function districtTail(primary: readonly string[], place: string, n = 3): string {
  const p = String(place ?? '').trim();
  const tails: string[] = [];
  for (const k of primary) {
    const t = String(k ?? '').trim();
    const tail = p && t.startsWith(p) ? t.slice(p.length).trim() : t.split(' ').pop() ?? '';
    if (!tail || tails.some((x) => x.includes(tail) || tail.includes(x))) continue;
    tails.push(tail);
    if (tails.length >= n) break;
  }
  return tails.join('·');
}

/* ───────────────────────────── 상세 title 뒤 검색어(seo.detail_title_kw_v2) ───────────────────────────── */

/**
 * 상세 title 종류(cl-core detailTitleKind) → 두 번째 자리에 올 수 있는 각도. 「분양가」(price)는 첫 자리 고정이라 여기 없다.
 *   정비(redev)·입주 후(move_in)는 title 문형이 따로라 대상이 아니다(빈 목록 = 종전 문형).
 */
export const DETAIL_TITLE_KW_ANGLES: Readonly<Record<string, readonly KwAngle[]>> = {
  sale: ['model_house', 'schedule', 'competition', 'location', 'presale_right', 'unsold'],
  model_house: ['model_house', 'schedule', 'competition', 'location', 'presale_right'],
  building: ['move_in', 'presale_right', 'location', 'model_house'],
};

/** 각도 대표 phrase 의 끝 낱말이 그 각도 꼬리면 그 낱말(「청약일정」·「견본주택」), 아니면 각도 대표어. */
function titleWordOf(p: KwAnglePlan): string {
  const last = String(p.primary ?? '').trim().split(/\s+/).pop() ?? '';
  const mods = [...ANGLE_MODIFIERS[p.angle]].sort((a, b) => b.length - a.length);
  for (const m of mods) if (last === m || (m.length >= 2 && last.endsWith(m))) return m;
  return ANGLE_TERM[p.angle];
}

/**
 * 순수 — 상세 title 두 번째 검색어(최대 n개 · 검색량 큰 순). «측정된» 각도만(volume > 0) — 측정이 없으면 [] 를 돌려
 *   호출부가 종전 단계 문형을 쓴다(추측으로 title 을 바꾸지 않는다).
 */
export function detailTitleKwWords(
  plan: Pick<SiteKeywordPlan, 'angles'> | null | undefined, kind: string, n = 2,
  /** KWX 레인 D — 각도 밖 꼬리(평면도·조감도) 측정 후보(detailTitleTailCandidates) · 같은 검색량 순서로 섞는다 */
  extra: ReadonlyArray<{ word: string; volume: number }> = [],
): string[] {
  const allowed = DETAIL_TITLE_KW_ANGLES[kind];
  if (!plan || !allowed || !allowed.length) return [];
  const rows: Array<{ w: string; v: number; o: number }> = [
    ...plan.angles
      .filter((a) => allowed.includes(a.angle) && a.volume != null && a.volume > 0)
      .map((a) => ({ w: titleWordOf(a), v: a.volume ?? 0, o: allowed.indexOf(a.angle) })),
    ...extra.filter((x) => x.word && x.volume > 0).map((x, i) => ({ w: x.word, v: x.volume, o: allowed.length + i })),
  ].sort((a, b) => b.v - a.v || a.o - b.o);
  const out: string[] = [];
  for (const r of rows) {
    const w = r.w;
    if (!w || w === '분양가' || out.some((o) => o.includes(w) || w.includes(o))) continue;
    out.push(w);
    if (out.length >= n) break;
  }
  return out;
}

/**
 * KWX 레인 D(실측 F11) — 각도 사전 밖 상세 꼬리(평면도·조감도) 를 제목 두 번째 검색어 «후보» 로.
 *   선택은 이 현장 측정 검색량(site_kick_keywords · ≥ 30 · 「< 10」 제외)뿐 — 측정이 없으면 후보 0(추측으로 title 을 바꾸지 않는다).
 *   사실 관문: 평면도 = 명시 연결 공고 주택형 · 조감도 = 공개 이미지(AI 상상도 아님). rank_lock 경로는 호출부가 이 함수까지 오지 않는다.
 */
export const DETAIL_TITLE_TAILS: Readonly<Record<string, readonly string[]>> = {
  sale: ['평면도', '조감도'],
  model_house: ['평면도', '조감도'],
  building: ['평면도'],
};
const TAIL_FACT: Readonly<Record<string, 'floorPlan' | 'aerial'>> = { 평면도: 'floorPlan', 조감도: 'aerial' };
export const DETAIL_TITLE_TAIL_MIN_VOLUME = 30;

export function detailTitleTailCandidates(
  rows: ReadonlyArray<Pick<SiteKwRow, 'modifier' | 'volume_total' | 'is_lt10'>>,
  kind: string,
  facts: { floorPlan?: boolean; aerial?: boolean } = {},
): Array<{ word: string; volume: number }> {
  const allow = DETAIL_TITLE_TAILS[kind] ?? [];
  const best = new Map<string, number>();
  for (const r of rows) {
    const m = String(r.modifier ?? '').trim();
    const v = Number(r.volume_total ?? 0);
    if (!allow.includes(m) || r.is_lt10 || !(v >= DETAIL_TITLE_TAIL_MIN_VOLUME) || !facts[TAIL_FACT[m]]) continue;
    best.set(m, Math.max(best.get(m) ?? 0, v));
  }
  return [...best.entries()].map(([word, volume]) => ({ word, volume })).sort((a, b) => b.volume - a.volume || a.word.localeCompare(b.word));
}

/** 이 현장 평면도·조감도 꼬리 측정 행(실패 = []). */
export async function loadSiteTitleTails(sb: any, siteId: string): Promise<SiteKwRow[]> {
  if (!siteId) return [];
  try {
    const { data, error } = await sb.from('site_kick_keywords').select('keyword, modifier, volume_total, is_lt10')
      .eq('site_id', siteId).eq('is_lt10', false).in('modifier', ['평면도', '조감도']).limit(20);
    return error || !Array.isArray(data) ? [] : (data as SiteKwRow[]);
  } catch { return []; }
}

/* ───────────────────────────── 이미지 alt ───────────────────────────── */

/**
 * 「{현장명} {각도어} {무엇}」 — what 에 이미 각도어가 있으면 한 번만 · 현장명이 없으면 각도어+무엇.
 *   예: imageAlt('트리븐 센텀', 'model_house', '위치 지도') → 「트리븐 센텀 모델하우스 위치 지도」
 */
export function imageAlt(siteName: string, angle: KwAngle, what: string): string {
  const name = String(siteName ?? '').replace(/\s+/g, ' ').trim();
  const w = String(what ?? '').replace(/\s+/g, ' ').trim();
  const term = ANGLE_TERM[angle] ?? '';
  const hasTerm = !term || norm(w).includes(norm(term)) || norm(name).endsWith(norm(term));
  const out = [name, hasTerm ? '' : term, w].filter(Boolean).join(' ');
  return out.length > 100 ? out.slice(0, 100).trim() : out;
}

/* ───────────────────────────── DB 로더 ───────────────────────────── */

const SITE_COLS = 'id, name, display_name, name_variants, region, sigungu, dong, lifecycle_stage, name_lead';
const TTL_MS = 10 * 60 * 1000;
const CACHE_MAX = 500;
const siteCache = new Map<string, { at: number; v: SiteKeywordPlan }>();
const districtCache = new Map<string, { at: number; v: { primary: string[]; secondaries: string[] } }>();
const remember = <V,>(m: Map<string, { at: number; v: V }>, k: string, v: V) => {
  if (m.size >= CACHE_MAX) m.delete(m.keys().next().value as string);
  m.set(k, { at: Date.now(), v });
};

/** 한 현장의 키워드 계획. 현장 없음·실패 = 빈 계획(names []) — 던지지 않는다. */
export async function loadSiteKeywordPlan(sb: any, siteId: string): Promise<SiteKeywordPlan> {
  const blank: SiteKeywordPlan = { siteId, names: [], angles: [], regionTerms: [] };
  if (!siteId) return blank;
  const c = siteCache.get(siteId);
  if (c && Date.now() - c.at < TTL_MS) return c.v;
  try {
    const { data: site, error } = await sb.from('apt_sites').select(SITE_COLS).eq('id', siteId).maybeSingle();
    if (error || !site) return blank;
    const [kick, pool] = await Promise.all([
      sb.from('site_kick_keywords').select('keyword, modifier, volume_total, is_lt10').eq('site_id', siteId).eq('is_lt10', false)
        .order('volume_total', { ascending: false, nullsFirst: false }).limit(80),
      sb.from('naver_keyword_pool').select('keyword, display, modifier, volume_total').eq('context', 'site').eq('seed', siteId).eq('relevant', true).limit(80),
    ]);
    const v = buildSiteKeywordPlan(site as KickSiteInput, (kick?.data ?? []) as SiteKwRow[], (pool?.data ?? []) as PoolPhraseRow[]);
    remember(siteCache, siteId, v);
    return v;
  } catch {
    return blank;
  }
}

/** 시군구 키워드 계획 — 실측(그 지역) 검색량 순 → 모자라면 전역 실측 순서. 실패 = 전역 순서만. */
export async function loadDistrictKeywordPlan(sb: any, sido: string, sigungu: string): Promise<{ primary: string[]; secondaries: string[] }> {
  const key = `${sido}|${sigungu}`;
  const c = districtCache.get(key);
  if (c && Date.now() - c.at < TTL_MS) return c.v;
  let rows: DistrictPoolRow[] = [];
  try {
    const keys = districtRegionKeys(sido, sigungu);
    if (keys.length) {
      // naver_keyword_pool.region 은 원 표기(공백 없음) — 키 그대로 대조 · 지역당 수백 행이라 1,000 상한 안.
      const { data, error } = await sb.from('naver_keyword_pool').select('keyword, display, region, modifier, volume_total, lt10')
        .eq('context', 'region').eq('relevant', true).eq('lt10', false).in('region', keys).in('modifier', DISTRICT_MODIFIERS as string[])
        .order('volume_total', { ascending: false, nullsFirst: false }).limit(500);
      if (!error && Array.isArray(data)) rows = data as DistrictPoolRow[];
    }
  } catch { /* 전역 순서 */ }
  const v = buildDistrictKeywordPlan(sido, sigungu, rows);
  remember(districtCache, key, v);
  return v;
}
